import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MapPin, CalendarDays } from "lucide-react";
import { KEVALTEK_LOGO, CCTV_IMG, REGISTRATION_OPEN } from "@/data";

const taglineLines = ["SEE IT.", "EXPERIENCE IT.", "GROW TOGETHER."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};
const lineWrap = { hidden: {}, show: {} };
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  const scrollToRegister = () =>
    document.getElementById(REGISTRATION_OPEN ? "register" : "gallery")
      ?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden bg-ink" data-testid="hero">
      {/* Parallax CCTV backdrop */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img src={CCTV_IMG} alt="" className="h-full w-full object-cover object-center opacity-40" />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-10 bg-gradient-to-b from-ink/70 via-ink/60 to-ink"
      />
      <div className="absolute inset-0 z-10 bg-[radial-gradient(ellipse_at_top_right,rgba(255,87,34,0.18),transparent_55%)]" />

      <div className="relative z-20 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-6 pt-28 pb-16 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-orange" />
          <span className="overline text-orange">{REGISTRATION_OPEN ? "First time ever in Mysore" : "Mysore 2026 · Event Concluded"}</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92, filter: "blur(8px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="mb-8"
        >
          <img src={KEVALTEK_LOGO} alt="KevalTek Expo" className="h-24 w-auto md:h-36 lg:h-44" />
        </motion.div>

        <p className="mb-6 max-w-xl font-sans text-base text-muted-foreground md:text-lg">
          {REGISTRATION_OPEN
            ? "Mysore's biggest CCTV & Surveillance showcase — major brands under one roof. Register as a visitor to explore, evaluate and grow together."
            : "Mysore's first CCTV & Surveillance showcase brought major brands under one roof on 30 & 31 July 2026. Thank you to everyone who joined us — relive the highlights below."}
        </p>

        {/* Kinetic masked line reveal */}
        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-display text-5xl font-semibold leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-8xl"
          data-testid="hero-tagline"
        >
          {taglineLines.map((t, i) => (
            <span key={i} className="block overflow-hidden py-0.5">
              <motion.span
                variants={line}
                className={`block ${i === 2 ? "text-chrome" : ""}`}
              >
                {t}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          variants={lineWrap}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-12 flex flex-col items-start gap-6 sm:flex-row sm:items-center"
        >
          <button
            onClick={scrollToRegister}
            className="group inline-flex items-center gap-3 rounded-sm bg-orange px-7 py-4 font-mono-tech text-sm font-medium uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:-translate-y-1 hover:bg-orange-hover"
            data-testid="hero-register-btn"
          >
            {REGISTRATION_OPEN ? "Visitor Registration" : "View Highlights"}
            <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
          </button>

          <div className="flex flex-col gap-2 border-l border-white/10 pl-6 font-mono-tech text-xs text-muted-foreground">
            <span className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-orange" /> 30 &amp; 31 JULY 2026
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-orange" /> ARCOR HOTELS, METAGALLI, MYSURU
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
